import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  Building2, 
  Search,
  Key,
  Eye,
  Mail,
  Calendar,
  Copy,
  Check,
  Trash2,
  Loader2,
  ShieldAlert,
  Shield,
  CheckCircle2,
  XCircle,
  KeyRound,
  Pencil,
  Save,
  Phone,
  Globe,
  MapPin,
  User,
  Clock
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from "../../components/ui/dialog";
import { AdminLayout } from "../../components/AdminLayout";
import { getAdminEntities, getAdminEntityDetail, requestDeletion, adminApproveKeyRegen, adminRejectKeyRegen, updateAdminEntity } from "../../lib/adminApi";
import { toast } from "sonner";

const TIMEZONE_OPTIONS = [
  { value: "Atlantic/Canary", label: "GMT+0 - Canàries (WET)" },
  { value: "Europe/Madrid", label: "GMT+1 - Espanya peninsular (CET)" },
  { value: "Europe/Paris", label: "GMT+1 - França (CET)" },
  { value: "Europe/Berlin", label: "GMT+1 - Alemanya (CET)" },
  { value: "Europe/Rome", label: "GMT+1 - Itàlia (CET)" },
  { value: "Europe/Brussels", label: "GMT+1 - Bèlgica (CET)" },
  { value: "Europe/Amsterdam", label: "GMT+1 - Holanda (CET)" },
  { value: "Europe/Lisbon", label: "GMT+0 - Portugal (WET)" },
  { value: "Europe/London", label: "GMT+0 - Regne Unit (GMT)" },
  { value: "Europe/Athens", label: "GMT+2 - Grècia (EET)" },
  { value: "Europe/Bucharest", label: "GMT+2 - Romania (EET)" },
  { value: "Europe/Helsinki", label: "GMT+2 - Finlàndia (EET)" },
  { value: "America/Mexico_City", label: "GMT-6 - Mèxic (CST)" },
  { value: "America/Bogota", label: "GMT-5 - Colòmbia (COT)" },
  { value: "America/Argentina/Buenos_Aires", label: "GMT-3 - Argentina (ART)" },
  { value: "America/Santiago", label: "GMT-4 - Xile (CLT)" },
];

export default function AdminEntitiesPage() {
  const { t } = useTranslation();
  const [entities, setEntities] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // State
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);

  // Detail/Edit modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailEntity, setDetailEntity] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    loadEntities();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredEntities(
        entities.filter(e => 
          e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.email.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredEntities(entities);
    }
  }, [searchTerm, entities]);

  const loadEntities = async () => {
    try {
      const response = await getAdminEntities();
      setEntities(response.data);
    } catch (error) {
      toast.error(t('adminEntities.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntity = async (entity) => {
    setEntityToDelete(entity);
    setShowDeleteDialog(true);
  };

  const confirmDeleteEntity = async () => {
    if (!entityToDelete) return;
    setDeletingId(entityToDelete.id);
    setShowDeleteDialog(false);
    try {
      const res = await requestDeletion("entity", entityToDelete.id);
      toast.success(res.data?.message || t('adminEntities.deletionEmailSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminEntities.deletionError'));
    } finally {
      setDeletingId(null);
      setEntityToDelete(null);
    }
  };

  const handleApproveKeyRegen = async (entity) => {
    if (!window.confirm(t('adminEntities2.confirmRegen', { name: entity.name }))) return;
    setApprovingId(entity.id);
    try {
      await adminApproveKeyRegen(entity.id);
      toast.success(t('adminEntities.keysRegenerated'));
      loadEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminEntities.regenError'));
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectKeyRegen = async (entity) => {
    if (!window.confirm(t('adminEntities2.confirmReject', { name: entity.name }))) return;
    setRejectingId(entity.id);
    try {
      await adminRejectKeyRegen(entity.id);
      toast.success(t('adminEntities.regenRejected'));
      loadEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('scrutinyRequests.rejectError'));
    } finally {
      setRejectingId(null);
    }
  };

  const pendingRegenCount = entities.filter(e => e.key_regen_status === "pending").length;

  const handleViewDetail = async (entity) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    setEditing(false);
    try {
      const response = await getAdminEntityDetail(entity.id);
      setDetailEntity(response.data);
      setEditData({
        name: response.data.name || "",
        email: response.data.email || "",
        notification_email: response.data.notification_email || "",
        cif: response.data.cif || "",
        address: response.data.address || "",
        city: response.data.city || "",
        postal_code: response.data.postal_code || "",
        province: response.data.province || "",
        phone: response.data.phone || "",
        website: response.data.website || "",
        contact_person: response.data.contact_person || "",
        contact_phone: response.data.contact_phone || "",
        timezone: response.data.timezone || "Europe/Madrid",
        auto_dst: response.data.auto_dst ?? true,
        require_email: response.data.require_email ?? false,
      });
    } catch (error) {
      toast.error(t('adminEntities.detailsError'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const response = await updateAdminEntity(detailEntity.id, editData);
      setDetailEntity(response.data);
      setEditing(false);
      toast.success(t('adminEntities.updated'));
      loadEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminEntities.updateError'));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8" data-testid="admin-entities-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div>
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-heading font-bold text-white">
                {t('adminEntities2.title')}
              </h1>
              {pendingRegenCount > 0 && (
                <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-bold animate-pulse" data-testid="pending-regen-badge">
                  {pendingRegenCount} {pendingRegenCount > 1 ? t('adminEntities2.regenPendings') : t('adminEntities2.regenPending')}
                </span>
              )}
            </div>
            <p className="text-slate-400 mt-1">
              {t('adminEntities2.subtitle')}
            </p>
          </div>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              placeholder={t('admin.searchEntities')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="admin-search-entities"
            />
          </div>
        </motion.div>

        {/* Entities List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredEntities.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 bg-slate-800 border border-slate-700 rounded-lg"
          >
            <Building2 className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">{t('adminEntities2.noEntities')}</h3>
            <p className="text-slate-400">
              {searchTerm ? t('adminEntities2.noFilterResults') : t('adminEntities2.noEntitiesYet')}
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">
                      {t('adminEntities2.entityColumn')}
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">
                      {t('adminEntities2.emailColumn')}
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">
                      {t('adminEntities2.keyStatus')}
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">
                      {t('adminEntities2.registrationDate')}
                    </th>
                    <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">
                      {t('adminEntities2.actionsColumn')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntities.map((entity) => (
                    <tr 
                      key={entity.id} 
                      className={`border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${entity.key_regen_status === "pending" ? "bg-orange-500/5" : ""}`}
                      data-testid={`admin-entity-row-${entity.id}`}
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-slate-400" />
                          </div>
                          <span className="font-medium text-white cursor-pointer hover:text-amber-400 transition-colors" onClick={() => handleViewDetail(entity)} data-testid={`admin-entity-name-${entity.id}`}>{entity.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-slate-300">
                          <Mail className="h-4 w-4 text-slate-500" />
                          {entity.email}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {entity.key_regen_status === "pending" ? (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-500 text-white" data-testid={`key-regen-status-${entity.id}`}>
                            {t('adminEntities2.regenPending')}
                          </span>
                        ) : entity.key_regen_status === "approved" ? (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white" data-testid={`key-regen-status-${entity.id}`}>
                            {t('adminEntities2.keyPendingPickup')}
                          </span>
                        ) : (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-emerald-100" data-testid={`key-regen-status-${entity.id}`}>
                            {t('adminEntities2.normal')}
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-slate-300">
                          <Calendar className="h-4 w-4 text-slate-500" />
                          {entity.created_at 
                            ? new Date(entity.created_at).toLocaleDateString("ca-ES")
                            : "N/A"}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-end gap-2">
                          {entity.key_regen_status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApproveKeyRegen(entity)}
                                disabled={approvingId === entity.id}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`admin-approve-regen-${entity.id}`}
                              >
                                {approvingId === entity.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" />{t('adminEntities2.approve')}</>}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectKeyRegen(entity)}
                                disabled={rejectingId === entity.id}
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                data-testid={`admin-reject-regen-${entity.id}`}
                              >
                                {rejectingId === entity.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><XCircle className="h-4 w-4 mr-1" />{t('adminEntities2.rejectBtn')}</>}
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(entity)}
                            className="text-slate-400 hover:text-white hover:bg-slate-700"
                            data-testid={`admin-view-entity-${entity.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {t('adminEntities2.view')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEntity(entity)}
                            disabled={deletingId === entity.id}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`admin-delete-entity-${entity.id}`}
                          >
                            {deletingId === entity.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Trash2 className="h-4 w-4 mr-1" />
                                {t('adminEntities2.delete')}
                              </>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>

      {/* Entity Detail/Edit Modal */}
      <Dialog open={showDetailModal} onOpenChange={(open) => { setShowDetailModal(open); if (!open) setEditing(false); }}>
        <DialogContent className="max-w-3xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Building2 className="h-5 w-5 text-amber-500" />
              {editing ? t('admin.editEntity') : t('admin.entityDetails')}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : detailEntity && (
            <div className="space-y-5">
              {editing ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.nameLabel')}</Label>
                      <Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-name" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.emailLabel')}</Label>
                      <Input value={editData.email} onChange={(e) => setEditData({...editData, email: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-email" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">{t('settings.notificationEmail')}</Label>
                    <Input value={editData.notification_email} onChange={(e) => setEditData({...editData, notification_email: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" placeholder={t('settings.notificationEmailPlaceholder')} data-testid="admin-edit-entity-notification-email" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">CIF / NIF</Label>
                    <Input value={editData.cif} onChange={(e) => setEditData({...editData, cif: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-cif" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">{t('adminEntities2.addressLabel')}</Label>
                    <Input value={editData.address} onChange={(e) => setEditData({...editData, address: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-address" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.cityLabel')}</Label>
                      <Input value={editData.city} onChange={(e) => setEditData({...editData, city: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-city" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.postalCodeLabel')}</Label>
                      <Input value={editData.postal_code} onChange={(e) => setEditData({...editData, postal_code: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-postal" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.provinceLabel')}</Label>
                      <Input value={editData.province} onChange={(e) => setEditData({...editData, province: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-province" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.phoneLabel')}</Label>
                      <Input value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-phone" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.webLabel')}</Label>
                      <Input value={editData.website} onChange={(e) => setEditData({...editData, website: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-website" />
                    </div>
                  </div>
                  <div className="border-t border-slate-700 pt-4">
                    <Label className="text-slate-300 text-sm flex items-center gap-2 mb-2"><Clock className="h-4 w-4" /> {t('adminEntities2.timezoneLabel')}</Label>
                    <select
                      value={editData.timezone || "Europe/Madrid"}
                      onChange={(e) => setEditData({...editData, timezone: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="admin-edit-entity-timezone"
                    >
                      {TIMEZONE_OPTIONS.map(tz => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={editData.auto_dst}
                        onClick={() => setEditData({...editData, auto_dst: !editData.auto_dst})}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${editData.auto_dst ? 'bg-amber-500' : 'bg-slate-600'}`}
                        data-testid="admin-edit-entity-auto-dst"
                      >
                        <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
                          style={{ transform: editData.auto_dst ? 'translateX(18px)' : 'translateX(2px)' }}
                        />
                      </button>
                      <span className="text-sm text-slate-400">{t('adminEntities2.autoSummerTime')}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={editData.require_email}
                        onClick={() => setEditData({...editData, require_email: !editData.require_email})}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${editData.require_email ? 'bg-amber-500' : 'bg-slate-600'}`}
                        data-testid="admin-edit-entity-require-email"
                      >
                        <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
                          style={{ transform: editData.require_email ? 'translateX(18px)' : 'translateX(2px)' }}
                        />
                      </button>
                      <span className="text-sm text-slate-400">{t('adminEntities2.requireEmail')}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.contactPersonLabel')}</Label>
                      <Input value={editData.contact_person} onChange={(e) => setEditData({...editData, contact_person: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-contact" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminEntities2.contactPhoneLabel')}</Label>
                      <Input value={editData.contact_phone} onChange={(e) => setEditData({...editData, contact_phone: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-entity-contact-phone" />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveEdit} disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-save-entity-btn">
                      {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      {t('adminEntities2.save')}
                    </Button>
                    <Button variant="outline" onClick={() => setEditing(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">{t('common.cancel')}</Button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{detailEntity.name}</h3>
                      <p className="text-slate-400 text-sm">{detailEntity.email}</p>
                      {detailEntity.notification_email && (
                        <p className="text-slate-500 text-xs mt-1">{t('settings.notificationEmail')}: {detailEntity.notification_email}</p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-slate-400 hover:text-white" data-testid="admin-edit-entity-btn">
                      <Pencil className="h-4 w-4 mr-1" />{t('adminEntities2.edit')}
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <InfoField icon={<Building2 className="h-4 w-4" />} label={t('adminEntities2.cifLabel')} value={detailEntity.cif} t={t} />
                    <InfoField icon={<Phone className="h-4 w-4" />} label={t('adminEntities2.phoneLabel')} value={detailEntity.phone} t={t} />
                    <InfoField icon={<MapPin className="h-4 w-4" />} label={t('adminEntities2.addressLabel')} value={detailEntity.address} t={t} />
                    <InfoField icon={<MapPin className="h-4 w-4" />} label={t('adminEntities2.cityLabel')} value={detailEntity.city} t={t} />
                    <InfoField icon={<MapPin className="h-4 w-4" />} label={t('adminEntities2.postalCodeLabel')} value={detailEntity.postal_code} t={t} />
                    <InfoField icon={<MapPin className="h-4 w-4" />} label={t('adminEntities2.provinceLabel')} value={detailEntity.province} t={t} />
                    <InfoField icon={<Globe className="h-4 w-4" />} label={t('adminEntities2.webLabel')} value={detailEntity.website} isLink t={t} />
                    <InfoField icon={<Calendar className="h-4 w-4" />} label={t('adminEntities2.registrationDateLabel')} value={detailEntity.created_at ? new Date(detailEntity.created_at).toLocaleDateString("ca-ES") : "—"} t={t} />
                  </div>

                  <div className="border-t border-slate-700 pt-4">
                    <p className="text-sm font-medium text-slate-400 mb-3">{t('adminEntities2.timezoneLabel')}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <InfoField icon={<Clock className="h-4 w-4" />} label={t('adminEntities2.timezoneLabel')} value={TIMEZONE_OPTIONS.find(tz => tz.value === detailEntity.timezone)?.label || detailEntity.timezone || "Europe/Madrid"} t={t} />
                      <InfoField icon={<Clock className="h-4 w-4" />} label={t('adminEntities2.autoSummerTime')} value={detailEntity.auto_dst !== false ? t('adminEntities2.activated') : t('adminEntities2.deactivated')} t={t} />
                      <InfoField icon={<Mail className="h-4 w-4" />} label={t('adminEntities2.requireEmail')} value={detailEntity.require_email ? t('adminEntities2.activated') : t('adminEntities2.deactivated')} t={t} />
                    </div>
                  </div>

                  <div className="border-t border-slate-700 pt-4">
                    <p className="text-sm font-medium text-slate-400 mb-3">{t('adminEntities2.contactPerson')}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <InfoField icon={<User className="h-4 w-4" />} label={t('adminEntities2.contactPersonLabel')} value={detailEntity.contact_person} t={t} />
                      <InfoField icon={<Phone className="h-4 w-4" />} label={t('adminEntities2.contactPhoneLabel')} value={detailEntity.contact_phone} t={t} />
                    </div>
                  </div>

                  <div className="border-t border-slate-700 pt-4">
                    <p className="text-sm font-medium text-slate-400 mb-3">Contracte DPA (RGPD)</p>
                    <div className="grid grid-cols-2 gap-4">
                      {detailEntity.dpa_accepted_at ? (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-900/50 text-emerald-300">
                              <Shield className="h-3 w-3" /> Acceptat
                            </span>
                          </div>
                          <InfoField icon={<Calendar className="h-4 w-4" />} label="Data d'acceptació" value={new Date(detailEntity.dpa_accepted_at).toLocaleString("ca-ES")} t={t} />
                        </>
                      ) : (
                        <div className="col-span-2">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-900/50 text-amber-300">
                            Pendent d'acceptació
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) setEntityToDelete(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              {t('adminEntities2.deleteDialogTitle')}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {t('adminEntities2.deleteDialogDesc')}
            </DialogDescription>
          </DialogHeader>
          {entityToDelete && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-white font-medium">{entityToDelete.name}</p>
                <p className="text-slate-400 text-sm">{entityToDelete.email}</p>
              </div>
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 space-y-2">
                <p className="text-red-300 text-sm font-semibold">{t('adminEntities2.deleteWarningTitle')}</p>
                <ul className="text-red-200 text-sm space-y-1 list-disc list-inside">
                  <li>{t('adminEntities2.deleteWarningConsultes')}</li>
                  <li>{t('adminEntities2.deleteWarningCensus')}</li>
                  <li>{t('adminEntities2.deleteWarningVotes')}</li>
                  <li>{t('adminEntities2.deleteWarningResults')}</li>
                  <li>{t('adminEntities2.deleteWarningKeys')}</li>
                </ul>
              </div>
              <p className="text-slate-400 text-xs">{t('adminEntities2.deleteEmailNotice')}</p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => { setShowDeleteDialog(false); setEntityToDelete(null); }}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="delete-cancel-btn"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={confirmDeleteEntity}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-confirm-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('adminEntities2.deleteConfirmBtn')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function InfoField({ icon, label, value, isLink, t }) {
  return (
    <div className="bg-slate-700/50 rounded-lg p-3">
      <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
        {icon} {label}
      </div>
      {value ? (
        isLink ? (
          <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-blue-400 text-sm hover:underline">{value}</a>
        ) : (
          <p className="text-white text-sm">{value}</p>
        )
      ) : (
        <p className="text-slate-500 text-sm italic">{t ? t('adminEntities2.notSpecified') : '—'}</p>
      )}
    </div>
  );
}
